/**
 * Recharge Widget V2 Integration
 * Handles integration between Recharge subscription widget v2 and theme's add to cart functionality
 * 
 * This script bridges the gap between Recharge's new widget v2 and your existing cart system.
 */

class RechargeWidgetV2Integration {
  constructor() {
    this.widget = null;
    this.addToCartButton = null;
    this.productForm = null;
    this.currentSelection = null;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    // Wait for the Recharge widget to load
    this.waitForWidget();
  }

  waitForWidget() {
    const checkWidget = () => {
      this.widget = document.querySelector('recharge-subscription-widget');
      if (this.widget) {
        console.log('Recharge Widget V2 found, setting up integration...');
        this.setupIntegration();
      } else {
        // Check again in 100ms
        setTimeout(checkWidget, 100);
      }
    };
    
    checkWidget();
  }

  setupIntegration() {
    // Find add to cart buttons (both existing product form and standalone recharge button)
    this.addToCartButton = document.querySelector('[data-add-to-cart-button]');
    this.rechargeButton = document.querySelector('[data-recharge-button]');
    this.productForm = document.querySelector('product-form form');
    
    // Determine which scenario we're in
    this.hasProductForm = !!(this.addToCartButton && this.productForm);
    this.hasStandaloneButton = !!this.rechargeButton;
    
    if (!this.hasProductForm && !this.hasStandaloneButton) {
      console.warn('No Recharge integration found - neither product form nor standalone button detected');
      return;
    }

    // Listen for Recharge widget events
    this.setupRechargeEventListeners();
    
    // Setup handlers for whichever buttons are present
    if (this.hasProductForm) {
      this.setupAddToCartHandler();
    }
    
    if (this.hasStandaloneButton) {
      this.setupRechargeButtonHandler();
    }
    
    // Initial state update
    this.updateAddToCartButton();
    
    // Force enable button after a short delay to ensure Recharge widget is fully loaded
    setTimeout(() => {
      console.log('Force updating button after delay...');
      this.updateAddToCartButton();
    }, 1000);
    
    // Also try again after 3 seconds in case Recharge widget takes longer to load
    setTimeout(() => {
      console.log('Final button update attempt...');
      this.updateAddToCartButton();
    }, 3000);
    
    this.isInitialized = true;
    console.log('Recharge Widget V2 integration initialized', {
      hasProductForm: this.hasProductForm,
      hasStandaloneButton: this.hasStandaloneButton
    });
  }

  setupRechargeEventListeners() {
    // Listen for Recharge widget's custom events
    document.addEventListener('recharge:subscription:selected', (event) => {
      console.log('Recharge subscription selected:', event.detail);
      this.handleRechargeSelection(event.detail);
    });

    document.addEventListener('recharge:variant:changed', (event) => {
      console.log('Recharge variant changed:', event.detail);
      this.handleVariantChange(event.detail);
    });

    // Listen for Recharge widget's internal events
    document.addEventListener('recharge:widget:ready', (event) => {
      console.log('Recharge widget ready:', event.detail);
      this.updateAddToCartButton();
    });

    // Also listen for DOM changes in the widget
    this.observeWidgetChanges();
    
    // Listen for clicks within the widget
    this.setupWidgetClickListeners();
  }

  setupWidgetClickListeners() {
    if (!this.widget) return;

    this.widget.addEventListener('click', (event) => {
      // Check if a subscription option was clicked
      const option = event.target.closest('[data-selling-plan], [data-variant-id], .subscription-option');
      if (option) {
        // Small delay to allow Recharge widget to update its state
        setTimeout(() => {
          this.updateAddToCartButton();
        }, 100);
      }
    });
  }

  observeWidgetChanges() {
    if (!this.widget) return;

    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          // Check if the mutation affects selection state
          const target = mutation.target;
          if (target.hasAttribute('data-selected') || 
              target.hasAttribute('data-variant-id') || 
              target.hasAttribute('data-selling-plan') ||
              target.classList.contains('selected') ||
              target.classList.contains('active')) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        this.updateAddToCartButton();
      }
    });

    observer.observe(this.widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-selected', 'data-variant-id', 'data-selling-plan', 'class']
    });
  }

  handleRechargeSelection(detail) {
    this.currentSelection = detail;
    this.updateAddToCartButton();
  }

  handleVariantChange(detail) {
    this.currentSelection = detail;
    this.updateAddToCartButton();
  }

  updateAddToCartButton() {
    if (!this.isInitialized) return;

    // Get current selection from widget
    const selection = this.getCurrentSelection();
    
    if (selection) {
      console.log('Selection found, updating buttons:', selection);
      // Update product form button if it exists
      if (this.hasProductForm) {
        this.updateProductFormButton(selection);
      }
      
      // Update standalone recharge button if it exists
      if (this.hasStandaloneButton) {
        this.updateRechargeButton(selection);
      }
    } else {
      console.log('No selection found, enabling buttons by default');
      // No selection found - enable buttons by default (Recharge widget might have default selection)
      if (this.hasProductForm) {
        this.addToCartButton.disabled = false;
        const addTextElement = this.addToCartButton.querySelector('.add-text');
        if (addTextElement) {
          addTextElement.innerHTML = `${window.variantStrings?.addToCart || 'Add to Cart'} <span class="font-family-heading lh-1"> <span class="mx-2">|</span> (per shipment)</span>`;
        }
      }
      if (this.hasStandaloneButton) {
        this.rechargeButton.disabled = false;
        const addTextElement = this.rechargeButton.querySelector('.add-text');
        if (addTextElement) {
          const buttonText = this.rechargeButton.getAttribute('data-button-text') || 'Add to Cart';
          addTextElement.innerHTML = buttonText;
        }
      }
    }
    
    // Always ensure buttons are enabled if they exist (fallback safety)
    if (this.hasStandaloneButton && this.rechargeButton.disabled) {
      console.log('Force enabling recharge button as fallback');
      this.rechargeButton.disabled = false;
    }
  }

  updateProductFormButton(selection) {
    if (!this.addToCartButton) return;

    // Update button text with current price
    const priceText = selection.price ? `| ${selection.price}` : '';
    const buttonText = `${window.variantStrings?.addToCart || 'Add to Cart'} <span class="font-family-heading lh-1"> <span class="mx-2">|</span> ${priceText} (per shipment)</span>`;
    
    const addTextElement = this.addToCartButton.querySelector('.add-text');
    if (addTextElement) {
      addTextElement.innerHTML = buttonText;
    }

    // Enable/disable button based on availability
    if (selection.available === false) {
      this.addToCartButton.disabled = true;
      const addTextElement = this.addToCartButton.querySelector('.add-text');
      if (addTextElement) {
        addTextElement.innerHTML = window.variantStrings?.soldOut || 'Sold Out';
      }
    } else {
      this.addToCartButton.disabled = false;
    }

    // Update sticky add to cart buttons
    this.updateStickyButtons(selection);
  }

  updateRechargeButton(selection) {
    if (!this.rechargeButton) return;

    const buttonText = this.rechargeButton.getAttribute('data-button-text') || 'Add to Cart';
    const soldOutText = this.rechargeButton.getAttribute('data-sold-out-text') || 'Sold Out';
    const showPrice = this.rechargeButton.getAttribute('data-show-price') === 'true';

    let displayText = buttonText;
    
    if (selection.available === false) {
      displayText = soldOutText;
      this.rechargeButton.disabled = true;
    } else {
      this.rechargeButton.disabled = false;
      
      if (showPrice && selection.price) {
        displayText = `${buttonText} <span class="font-family-heading lh-1"> <span class="mx-2">|</span> ${selection.price} (per shipment)</span>`;
      }
    }

    const addTextElement = this.rechargeButton.querySelector('.add-text');
    if (addTextElement) {
      addTextElement.innerHTML = displayText;
    }
  }

  updateStickyButtons(selection) {
    const stickyButtons = document.querySelectorAll('[data-atc-out]');
    stickyButtons.forEach(button => {
      const priceText = selection.price ? `| ${selection.price}` : '';
      const buttonText = `${window.variantStrings?.addToCart || 'Add to Cart'} <span class="font-family-heading lh-1"> <span class="mx-2">|</span> ${priceText} (per shipment)</span>`;
      
      const addTextElement = button.querySelector('.add-text');
      if (addTextElement) {
        addTextElement.innerHTML = buttonText;
      }
      
      button.disabled = selection.available === false;
    });
  }

  getCurrentSelection() {
    if (!this.widget) {
      console.log('No Recharge widget found');
      return null;
    }

    console.log('Looking for Recharge widget selection...');
    
    // Try multiple methods to get the current selection
    let selectedOption = null;
    
    // Method 1: Look for elements with data-selected="true"
    selectedOption = this.widget.querySelector('[data-selected="true"]');
    console.log('Method 1 (data-selected="true"):', selectedOption);
    
    // Method 2: Look for elements with class "selected" or "active"
    if (!selectedOption) {
      selectedOption = this.widget.querySelector('.selected, .active');
      console.log('Method 2 (.selected, .active):', selectedOption);
    }
    
    // Method 3: Look for checked radio buttons or inputs
    if (!selectedOption) {
      selectedOption = this.widget.querySelector('input:checked');
      console.log('Method 3 (input:checked):', selectedOption);
    }
    
    // Method 4: Look for elements with data-variant-id or data-selling-plan
    if (!selectedOption) {
      selectedOption = this.widget.querySelector('[data-variant-id], [data-selling-plan]');
      console.log('Method 4 ([data-variant-id], [data-selling-plan]):', selectedOption);
    }
    
    // Method 5: Look for any clickable elements that might be selected
    if (!selectedOption) {
      selectedOption = this.widget.querySelector('[role="button"][aria-pressed="true"]');
      console.log('Method 5 ([role="button"][aria-pressed="true"]):', selectedOption);
    }
    
    // Method 6: Look for elements with aria-selected="true"
    if (!selectedOption) {
      selectedOption = this.widget.querySelector('[aria-selected="true"]');
      console.log('Method 6 ([aria-selected="true"]):', selectedOption);
    }
    
    // Method 7: Look for any element that looks like a selected option
    if (!selectedOption) {
      const allOptions = this.widget.querySelectorAll('button, [role="button"], input, .option, .plan, .subscription-option');
      console.log('Method 7 (all options):', allOptions);
      
      for (let option of allOptions) {
        if (option.classList.contains('selected') || 
            option.classList.contains('active') || 
            option.classList.contains('chosen') ||
            option.getAttribute('aria-selected') === 'true' ||
            option.getAttribute('data-selected') === 'true') {
          selectedOption = option;
          console.log('Found selected option via Method 7:', selectedOption);
          break;
        }
      }
    }

    if (selectedOption) {
      console.log('Selected option found:', selectedOption);
      console.log('Selected option classes:', selectedOption.className);
      console.log('Selected option attributes:', Array.from(selectedOption.attributes).map(attr => `${attr.name}="${attr.value}"`));
      
      const variantId = selectedOption.getAttribute('data-variant-id') || 
                       selectedOption.getAttribute('value') ||
                       selectedOption.getAttribute('data-id') ||
                       this.extractVariantIdFromElement(selectedOption);
      
      const sellingPlanId = selectedOption.getAttribute('data-selling-plan') ||
                           selectedOption.getAttribute('data-plan-id') ||
                           selectedOption.getAttribute('data-selling-plan-id') ||
                           this.extractSellingPlanIdFromElement(selectedOption);
      
      const price = selectedOption.getAttribute('data-price') ||
                   selectedOption.querySelector('[data-price]')?.textContent ||
                   this.extractPriceFromElement(selectedOption);
      
      const available = selectedOption.getAttribute('data-available') !== 'false' &&
                       !selectedOption.classList.contains('disabled') &&
                       !selectedOption.disabled;
      
      console.log('Extracted data:', { variantId, sellingPlanId, price, available });
      
      return {
        variantId: variantId,
        sellingPlanId: sellingPlanId,
        price: price,
        available: available,
        element: selectedOption
      };
    }

    console.log('No selection found, trying global state...');
    // Fallback: Try to get selection from Recharge widget's global state
    return this.getSelectionFromGlobalState();
  }

  extractVariantIdFromElement(element) {
    // Look for variant ID in various attributes or data attributes
    const variantId = element.getAttribute('data-variant-id') ||
                     element.getAttribute('value') ||
                     element.getAttribute('data-id');
    
    if (variantId && /^\d+$/.test(variantId)) {
      return variantId;
    }
    
    return null;
  }

  extractSellingPlanIdFromElement(element) {
    // Look for selling plan ID in various attributes
    const sellingPlanId = element.getAttribute('data-selling-plan') ||
                         element.getAttribute('data-plan-id') ||
                         element.getAttribute('data-selling-plan-id');
    
    if (sellingPlanId && /^\d+$/.test(sellingPlanId)) {
      return sellingPlanId;
    }
    
    return null;
  }

  extractPriceFromElement(element) {
    // Look for price in various places
    const priceElement = element.querySelector('[data-price], .price, .rc-price') ||
                        element.querySelector('.rc-radio__price, .subscription-price');
    
    if (priceElement) {
      return priceElement.textContent.trim();
    }
    
    return null;
  }

  getSelectionFromGlobalState() {
    // Try to get selection from Recharge widget's global state
    if (window.SubscriptionWidgetConfig && window.SubscriptionWidgetConfig.shop) {
      console.log('Using global state fallback');
      
      // Extract variant ID from the widget's default-variant-id attribute
      const defaultVariantId = this.widget?.getAttribute('default-variant-id');
      
      // Try to get selling plan from the global config
      let sellingPlanId = null;
      let price = null;
      
      if (window.SubscriptionWidgetConfig.shopifyPlans && window.SubscriptionWidgetConfig.shopifyPlans.length > 0) {
        // Use the first selling plan as default
        sellingPlanId = window.SubscriptionWidgetConfig.shopifyPlans[0].id;
        console.log('Using default selling plan:', sellingPlanId);
      }
      
      // Try to get price from formatted prices
      if (defaultVariantId && window.SubscriptionWidgetConfig.formattedPrices) {
        const variantPrices = window.SubscriptionWidgetConfig.formattedPrices[defaultVariantId];
        if (variantPrices && variantPrices.sellingPlans && sellingPlanId) {
          const planPrices = variantPrices.sellingPlans[sellingPlanId];
          if (planPrices) {
            price = planPrices.price;
            console.log('Found price from global config:', price);
          }
        }
      }
      
      return {
        variantId: defaultVariantId,
        sellingPlanId: sellingPlanId,
        price: price,
        available: true
      };
    }
    
    return null;
  }

  setupAddToCartHandler() {
    if (!this.addToCartButton) return;

    // Remove existing event listeners to avoid conflicts
    const newButton = this.addToCartButton.cloneNode(true);
    this.addToCartButton.parentNode.replaceChild(newButton, this.addToCartButton);
    this.addToCartButton = newButton;
    
    // Add our custom handler
    this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
  }

  setupRechargeButtonHandler() {
    if (!this.rechargeButton) return;

    // Add click handler for standalone recharge button
    this.rechargeButton.addEventListener('click', this.handleRechargeButtonClick.bind(this));
  }

  handleAddToCart(event) {
    event.preventDefault();
    
    const selection = this.getCurrentSelection();
    if (!selection) {
      console.warn('No Recharge selection found');
      this.showError('Error', 'Please select a subscription option');
      return;
    }

    // Prepare cart data
    const cartData = this.prepareCartData(selection);
    
    // Submit to cart
    this.submitToCart(cartData, this.addToCartButton);
  }

  handleRechargeButtonClick(event) {
    event.preventDefault();
    
    const selection = this.getCurrentSelection();
    if (!selection) {
      console.warn('No Recharge selection found');
      this.showError('Error', 'Please select a subscription option');
      return;
    }

    // Prepare cart data for standalone button
    const cartData = this.prepareStandaloneCartData(selection);
    
    // Submit to cart
    this.submitToCart(cartData, this.rechargeButton);
  }

  prepareCartData(selection) {
    const formData = new FormData(this.productForm);
    
    // Update form data with Recharge selection
    if (selection.variantId) {
      formData.set('id', selection.variantId);
    }
    
    if (selection.sellingPlanId) {
      formData.set('selling_plan', selection.sellingPlanId);
    }

    // Convert FormData to object
    const cartItem = {};
    for (let [key, value] of formData.entries()) {
      cartItem[key] = value;
    }

    return cartItem;
  }

  prepareStandaloneCartData(selection) {
    // For standalone button, try to use the hidden form first
    const hiddenForm = document.querySelector(`#recharge-hidden-form-${this.rechargeButton.getAttribute('data-product-id')}`);
    
    if (hiddenForm) {
      const formData = new FormData(hiddenForm);
      
      // Update form data with Recharge selection
      if (selection.variantId) {
        formData.set('id', selection.variantId);
      }
      
      if (selection.sellingPlanId) {
        formData.set('selling_plan', selection.sellingPlanId);
      }

      // Convert FormData to object
      const cartItem = {};
      for (let [key, value] of formData.entries()) {
        cartItem[key] = value;
      }

      return cartItem;
    }
    
    // Fallback: create cart data from scratch
    const cartItem = {
      quantity: 1
    };
    
    // Add variant ID
    if (selection.variantId) {
      cartItem.id = selection.variantId;
    }
    
    // Add selling plan ID
    if (selection.sellingPlanId) {
      cartItem.selling_plan = selection.sellingPlanId;
    }

    return cartItem;
  }

  submitToCart(cartItem, submitButton) {
    // Show loading state
    submitButton.disabled = true;
    submitButton.classList.add('loading');

    // Show spinner if available
    const spinner = submitButton.querySelector('.spinner-border');
    if (spinner) {
      spinner.classList.remove('d-none');
    }

    const body = JSON.stringify({
      items: [cartItem],
      sections: `${((window.globalVariables?.template == 'cart') ? 'template-cart' : 'component-cart-drawer')},header`
    });

    fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: body
    })
    .then(response => response.json())
    .then(data => {
      if (data.errors) {
        this.showError('Add to Cart', data.errors);
        return;
      }

      // Success - update cart UI
      this.handleCartSuccess(data);
    })
    .catch(error => {
      console.error('Cart add error:', error);
      this.showError('Error', error.message || 'Failed to add to cart');
    })
    .finally(() => {
      // Remove loading state
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
      
      // Hide spinner if available
      const spinner = submitButton.querySelector('.spinner-border');
      if (spinner) {
        spinner.classList.add('d-none');
      }
    });
  }

  handleCartSuccess(data) {
    // Update cart drawer/sidebar
    const cartElement = document.querySelector('cart-form');
    if (cartElement && cartElement._updateCart) {
      cartElement._updateCart(data.sections, 'open_drawer');
    }

    // Dispatch custom event for other components
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    
    // Show success notification if available
    if (window.notificationEle) {
      window.notificationEle.updateNotification('Success', 'Item added to cart!', {
        type: 'success',
        timeout: 3000
      });
    }
  }

  showError(title, message) {
    console.error(`${title}:`, message);
    
    if (window.notificationEle) {
      window.notificationEle.updateNotification(title, message, {
        type: 'error',
        timeout: 5000
      });
    } else {
      // Fallback to alert if notification system not available
      alert(`${title}: ${Array.isArray(message) ? message.join(', ') : message}`);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new RechargeWidgetV2Integration();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RechargeWidgetV2Integration();
  });
} else {
  new RechargeWidgetV2Integration();
}
